import { buildProps } from '@zoryn/utils'

import type { ExtractPublicPropTypes, PropType } from 'vue'
import type { HttpRequest } from '@zoryn/network'
import type { Conversation, MessageRecord } from './types'
import type { GroupChatNode } from './utils/group-chat'
import type { ImRealtimeEvent } from './utils/message-api'
import { DEFAULT_MESSAGE_API_PREFIX } from './constants'

export type MessageCenterLoadOrg = () => Promise<GroupChatNode[]>

/** Host app uploads the file and returns a persisted URL (keep /infra/file out of the package). */
export type MessageCenterUploadFile = (
  file: File
) => Promise<{ url: string; name?: string }>

export const messageCenterProps = buildProps({
  http: {
    type: Object as PropType<HttpRequest>,
  },
  apiPrefix: {
    type: String,
    default: DEFAULT_MESSAGE_API_PREFIX,
  },
  autoLoad: {
    type: Boolean,
    default: true,
  },
  fallbackToMock: {
    type: Boolean,
    default: true,
  },
  authenticated: {
    type: Boolean,
    default: undefined,
  },
  bizTabLabels: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({}),
  },
  watermark: {
    type: String,
    default: 'Zoryn 消息中心',
  },
  realtimeEvent: {
    type: Object as PropType<ImRealtimeEvent | null>,
    default: null,
  },
  loadOrg: {
    type: Function as PropType<MessageCenterLoadOrg>,
  },
  uploadFile: {
    type: Function as PropType<MessageCenterUploadFile>,
  },
  /**
   * Select this conversation after load (e.g. opened from 通讯录)
   */
  conversationId: {
    type: String,
    default: '',
  },
} as const)

export type MessageCenterProps = ExtractPublicPropTypes<typeof messageCenterProps>

export const messageCenterEmits = {
  'open-business': (payload: { tab: string; message: MessageRecord }) =>
    Boolean(payload),
  'unread-change': (count: number) => typeof count === 'number',
  load: (conversations: Conversation[]) => Array.isArray(conversations),
  error: (error: Error) => error instanceof Error,
  send: (payload: { conversationId: string; text: string }) => Boolean(payload),
}

export type MessageCenterEmits = typeof messageCenterEmits
